import { useState, useEffect, useMemo } from 'react';
import SearchSection from './components/SearchSection';
import Sidebar from './components/Sidebar';
import MapView from './components/MapView';
import PlaceDetails from './components/PlaceDetails';
import MapCard from './components/MapCard';
import Header from './components/Header';
import Hero from './components/Hero';
import Featured from './components/Featured';
import Discover from './components/Discover';
import Experiences from './components/Experiences';
import Gallery from './components/Gallery';
import Footer from './components/Footer';
//import Contact from './components/Contact';
import Reveal from './components/Reveal';
import AppLoader from './components/AppLoader';
import { Box, Fab, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { getPlaces } from './api/places';
import { preloadImages } from './utils/preloadImages';
import './App.css';

// Imagen principal del Hero (misma URL que en Hero.jsx) para pre-cargar
const HERO_IMAGE_URL = 'https://firebasestorage.googleapis.com/v0/b/landingmunigr.appspot.com/o/Turismo%2FDescubr%C3%AD%20La%20Granja%20(foto%20principal)-destacado.jpg?alt=media&token=66fc4331-d56e-4421-baa7-074602de296d';

const PRELOAD_TIMEOUT_MS = 12000; // Máximo 12s esperando imágenes

function App() {
  const [places, setPlaces] = useState([]);
  const [appReady, setAppReady] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mapTarget, setMapTarget] = useState(null);
  const [mapCardPlace, setMapCardPlace] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useMediaQuery('(max-width:900px)');

  const filteredPlaces = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return places;
    return places.filter(
      (p) => p.title && p.title.toLowerCase().includes(q)
    );
  }, [places, searchQuery]);

  // Mostrar detalle solo si el lugar seleccionado sigue en la lista filtrada
  const effectiveSelectedPlace =
    selectedPlace && filteredPlaces.some((p) => p.id === selectedPlace.id)
      ? selectedPlace
      : null;
  const effectiveMapCardPlace =
    mapCardPlace && filteredPlaces.some((p) => p.id === mapCardPlace.id)
      ? mapCardPlace
      : null;

  useEffect(() => {
    let cancelled = false;

    async function loadThenPreload() {
      const data = await getPlaces();
      if (cancelled) return;
      setPlaces(Array.isArray(data) ? data : []);

      const imageUrls = [
        HERO_IMAGE_URL,
        ...(data || [])
          .filter((p) => p && p.image)
          .map((p) => p.image),
      ];

      await preloadImages(imageUrls, PRELOAD_TIMEOUT_MS);
      if (cancelled) return;
      setAppReady(true);
    }

    loadThenPreload();
    return () => { cancelled = true; };
  }, []);

  const handleSelect = (place) => {
    setSelectedPlace(place);
    setMapTarget(place); // Centrar y abrir popup
    if (isMobile) setDrawerOpen(false);
  };

  const handleViewMore = (place) => {
    if (isMobile) {
      setMapCardPlace(place);
    } else {
      setSelectedPlace(place);
    }
  };

  const handleCloseMapCard = () => {
    setMapCardPlace(null);
  };

  if (!appReady) {
    return <AppLoader />;
  }

  return (
    <>
      <Header />
      {/* Espacio para el nav fijo en móvil (toolbar 64 + barra progreso 2 = 66px) */}
      <Box component="main" sx={{ pt: { xs: 'calc(66px + env(safe-area-inset-top, 0px))', md: 0 } }}>
      <Hero />
      <Reveal delay={0} variant="zoom" distance={14}>
        <SearchSection
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      </Reveal>
      {/* Sección de mapa con menú lateral y detalles */}
      <Reveal delay={80} variant="fade-up" distance={18}>
        <section id="recorre" style={{ maxWidth: 1200, width: '100%', margin: '40px auto', padding: '0 16px', boxSizing: 'border-box', overflow: 'hidden' }}>
          <div style={{ display: 'flex', height: 640, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.08)', background: '#fff', position: 'relative', minWidth: 0, maxWidth: '100%' }}>
            {/* Sidebar */}
            {isMobile ? (
              <>
                <Fab
                  color="primary"
                  aria-label="menu"
                  style={{ 
                    position: 'absolute', 
                    top: 16, 
                    left: drawerOpen ? 'calc(100% - 60px)' : 16, // Se posiciona al final del menú o al inicio
                    zIndex: 2000,
                    transition: 'left 0.3s ease-in-out'
                  }}
                  onClick={() => setDrawerOpen(!drawerOpen)} // Toggle en lugar de solo abrir
                  size="small"
                >
                  {drawerOpen ? <CloseIcon /> : <MenuIcon />}
                </Fab>
                {/* Sidebar móvil que se desliza desde la izquierda dentro del contenedor */}
                <div style={{
                  position: 'absolute',
                  left: drawerOpen ? 0 : 'calc(-100% + 60px)', // Deja 60px de espacio para la cruz
                  top: 0,
                  width: 'calc(100% - 60px)', // Ancho responsivo menos 60px para la cruz
                  maxWidth: '320px', // Ancho máximo para pantallas grandes
                  height: '100%',
                  background: '#fff',
                  boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
                  zIndex: 1500, // Aumentado para estar por encima de los controles del mapa
                  transition: 'left 0.3s ease-in-out',
                  overflowY: 'auto'
                }}>
                  <Sidebar places={filteredPlaces} onSelect={handleSelect} searchQuery={searchQuery} />
                </div>
                {/* Overlay para cerrar el sidebar al hacer clic fuera */}
                {drawerOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0,0,0,0.3)',
                      zIndex: 1400 // Por debajo del sidebar pero por encima del mapa
                    }}
                    onClick={() => setDrawerOpen(false)}
                  />
                )}
              </>
            ) : (
              <Sidebar places={filteredPlaces} onSelect={handleSelect} searchQuery={searchQuery} />
            )}

            {/* Panel de detalles (centro) */}
            {!isMobile && effectiveSelectedPlace && (
              <div style={{ width: 360, minWidth: 320, background: '#fff', borderLeft: '1px solid #eee', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 12px rgba(0,0,0,0.06)' }}>
                <PlaceDetails place={effectiveSelectedPlace} onBack={() => setSelectedPlace(null)} />
              </div>
            )}
            
            {/* Mapa */}
            <div style={{ flex: 1, minWidth: 0, maxWidth: '100%', position: 'relative', overflow: 'hidden' }}>
              <MapView 
                center={[-31.014529402369533, -64.27261256314783]} 
                zoom={12} 
                places={filteredPlaces} 
                mapTarget={mapTarget} 
                setMapTarget={setMapTarget}
                onViewMore={handleViewMore}
                isMobile={isMobile}
              />
              {/* Card que aparece sobre el mapa - solo en móvil */}
              {isMobile && effectiveMapCardPlace && (
                <MapCard place={effectiveMapCardPlace} onClose={handleCloseMapCard} />
              )}
            </div>
          </div>
          {/* En móvil no mostramos PlaceDetails debajo del mapa: solo el popup en el mapa y, al tocar "Ver más", la MapCard sobre el mapa */}
        </section>
      </Reveal>
      {/* Resto de las secciones */}
      <Reveal delay={60} variant="slide-right" distance={22}>
        <Featured />
      </Reveal>
      <Reveal delay={60} variant="slide-left" distance={22}>
        <Discover />
      </Reveal>
      <Reveal delay={60} variant="fade-down" distance={16}>
        <Experiences />
      </Reveal>
      <Box id="galeria" component="section">
        <Reveal delay={60} variant="zoom" distance={14}>
          <Gallery />
        </Reveal>
      </Box>
      {/* Sección Sumate a La Granja - comentada
      <Reveal delay={60} variant="fade-up" distance={18}>
        <Contact />
      </Reveal>
      */}
      <Reveal delay={60} variant="slide-right" distance={18}>
        <Footer />
      </Reveal>
      </Box>
    </>
  );
}

export default App;
